/**
 * Turning an answer into a move on the graph — without the answer becoming one.
 *
 * The Ask channel is deliberately outside the record. It cannot search, so
 * nothing it says has passed the citation gate every source check in this app
 * rests on, and `engine/chat.ts` states the rule this module has to keep:
 * speculation that became a node "would be indistinguishable from reasoning the
 * graph paid for". That rule is why asking has been a cul-de-sac — a reader gets
 * a good answer and has no way back to the graph except retyping it.
 *
 * THE PRESS IS THE WAY BACK, and the shape of it is the whole design:
 *
 *   **The answer is an INPUT to a priced pass, never the output that lands.**
 *
 * `runOp` already takes an `extra` string appended verbatim to the user turn, so
 * "Challenge with this" runs a real challenge pass — same prompt, same gate, same
 * ledger row — that has merely been told what the reader found convincing. What
 * enters the record is the pass's own reasoning, paid for at the pass's own
 * price. The answer steers; it does not arrive.
 *
 * The other two presses open a box a person types into, and they open it EMPTY
 * of the answer — the editor on the node's own claim, the form on nothing. A
 * seeded box is that rule broken quietly: pre-fill the claim with three
 * paragraphs of unchecked chat and "a human writes what lands" costs one press
 * of a button the reader was already reaching for. So the answer stays in the
 * conversation and the reader writes the claim themselves. What these two save
 * is the walk — from an answer there was no route to either door except going to
 * find the card again — and each press scrolls its box onto the screen, because
 * the thread renders the card ABOVE the conversation and a box opened silently
 * behind the reader is no better than one that never opened.
 *
 * A PRESS IS DATA, for the reason `core/cues.ts` gives about its own actions
 * (AGENTS.md invariant 9). Nothing here is a function, so nothing in this module
 * can call a model; the UI reads the descriptor and dispatches, and every model
 * call still sits behind an explicit press by a person. A closure here would put
 * spending one `.map()` away from a render.
 *
 * This module imports NOTHING. It sits under the store and under the UI alike.
 */

/** The doors an answer can be walked back through. Each already exists — this
 *  module adds no way to change the graph, only a way to reach the ways that
 *  are already there. `diverge` is the branching one: the study's clearest text
 *  came from Ask, and a reader who wanted to branch on it had only Deepen, which
 *  goes further down the same line rather than off it. */
export type PromoteKind = 'challenge' | 'deepen' | 'diverge' | 'rewrite' | 'record'

/**
 * One press, as data.
 *
 * Deliberately the same shape as `CueAction`: a kind and a label, with the
 * lookup tables beside it rather than fields on it. A descriptor that carried
 * its own price would be a second place for the arithmetic to live, and this
 * codebase has a scar from exactly that — see the refutation gate in
 * `core/types.ts`, which was two copies until one of them drifted.
 */
export interface Promote {
  kind: PromoteKind
  /** What the button says. Names the act, and says the answer is its input:
   *  "with this" is doing real work in these strings. */
  label: string
  /**
   * What pressing it does, and what it does NOT do.
   *
   * Not a firing rule — unlike a cue, a press is offered because a node is in
   * hand rather than because a threshold was crossed, so there is no condition
   * to print. What it carries instead is the sentence a reader needs before
   * spending: which of these writes to the graph, and who wrote what it writes.
   */
  does: string
}

/**
 * Which verb's rates a press is quoted at, or null where nothing runs.
 *
 * The same table `core/cues.ts` keeps for its own actions, and for the same
 * reason: the UI needs one place to ask "what will this cost" and the answer has
 * to come from the same arithmetic every other price in the app goes through.
 *
 * `rewrite` IS NULL, and it was 'move' until review pointed out what that
 * actually claims. Re-deriving is a pass, but this press does not re-derive: it
 * pokes `sigRewrite` and a text box opens. The paid pass is `reviseNode`, which
 * runs when a person presses Re-derive inside that box, having written something
 * — so a figure here priced a press that spends nothing and left the press that
 * does spend without one. The node card had already settled this: it lists the
 * rewrite under its FREE tier and says "pressing re-derive afterwards is the paid
 * pass, and the editor prices it on its own button". Two surfaces disagreeing
 * about one act is the thing this module keeps being written to prevent.
 */
export const PROMOTE_PRICE_VERB: Record<PromoteKind, 'move' | null> = {
  challenge: 'move',
  deepen: 'move',
  diverge: 'move',
  rewrite: null,
  record: null,
}

/**
 * Which presses spend, kept separate from the table above for the reason
 * `CUE_ACTION_PAID` gives: a null price verb covers two opposite facts, "this
 * costs nothing" and "this costs something we cannot quote here". Only the first
 * is true of anything in this set today, and writing it out means the next press
 * added cannot quietly inherit the wrong one.
 */
export const PROMOTE_PAID: Record<PromoteKind, boolean> = {
  challenge: true,
  deepen: true,
  diverge: true,
  // Opening the editor calls no model. What the reader types may then be
  // expensive to act on, and the button that acts on it says so — see above.
  rewrite: false,
  record: false,
}

/**
 * What a press needs to know about where it would land.
 *
 * A union rather than an optional flag, so the seed question cannot be answered
 * by forgetting to ask it: a `node` does not typecheck without `isRoot`. The
 * cost is that a `ChatTarget` no longer goes straight in, which is the right
 * trade — the caller holds the node and this module deliberately holds nothing.
 */
export type PromoteTarget =
  { kind: 'node'; isRoot: boolean } | { kind: 'bridge' } | { kind: 'graph' }

/**
 * The presses on offer under a finished answer.
 *
 * ONE RULE DECIDES MOST OF THE SET: a press needs a node to act on, and only a
 * node-pinned conversation has one. `runOp`, `reviseNode` and the outcome form
 * are all addressed by node id; a graph-wide answer names no node and a bridge
 * names two, and picking an end for the reader would be the app guessing at the
 * one moment it is about to spend their money. Those conversations offer no
 * presses rather than a press that has to guess.
 *
 * THE SEED IS THE SECOND RULE, and it is the node card's, quoted: that card
 * renders the outcome form and the rewrite row behind `!isRoot`, because the
 * seed is the question this exploration asks rather than a claim. A result
 * cannot bear on a question, and rewriting it leaves the graph derived from
 * something that is no longer there. Offering them here and refusing on the
 * press would put two surfaces at odds about the same node — so this offers
 * what that card offers. Branching from a seed is ordinary, so those two stay.
 *
 * Returns a fresh array of fresh objects on every call. The set is small and
 * static, and handing out a shared reference would let a caller mutate the
 * table every other caller reads.
 */
export function promotesFor(target: PromoteTarget): Promote[] {
  if (target.kind !== 'node') return []
  const onSeed = target.isRoot
  return [
    {
      kind: 'challenge',
      label: 'Challenge with this',
      does: 'Runs a challenge pass on this claim, told what the answer above argued. The pass writes the node, not the answer.',
    },
    {
      kind: 'deepen',
      label: 'Deepen with this',
      does: 'Runs a deepen pass on this claim, told what the answer above argued. The pass writes the nodes, not the answer.',
    },
    // ONE diverge pass, told the answer as a direction — never a branch per idea
    // in the answer. What lands is the pass's own reframing, as many nodes as the
    // verb writes, and the answer stays its input (D42).
    {
      kind: 'diverge',
      label: 'Diverge with this',
      does: 'Runs one diverge pass on this claim, told what the answer above argued, to reframe it from elsewhere. The pass writes the nodes, not the answer.',
    },
    ...(onSeed ? [] : seedExcluded()),
  ]
}

/**
 * The two the seed does not get.
 *
 * A FUNCTION rather than a module constant, and that is the whole reason it is
 * written this way: a shared array spliced into every result would hand every
 * caller the same two objects to mutate, which is exactly what `promotesFor`
 * promises it does not do.
 */
function seedExcluded(): Promote[] {
  return [
    {
      kind: 'rewrite',
      label: 'Rewrite the claim',
      does: 'Brings the revision editor on this claim into view, with the conversation still below it. Writing costs nothing; the Re-derive button inside is the paid pass, and it prices itself.',
    },
    {
      kind: 'record',
      label: 'Record as result',
      does: 'Brings the outcome form into view, with the conversation still below it. What lands is the observation you type, which is why it outranks every rating on the graph.',
    },
  ]
}

/**
 * Which answer the row of presses acts on — or nothing, when no answer can
 * honestly claim it.
 *
 * ONE RULE, and it is deliberately the strictest one that can be stated in a
 * sentence: THE ROW IS OFFERED ONLY WHEN THE LAST THING IN THE CONVERSATION IS A
 * FINISHED ANSWER, AND IT IS ABOUT THAT ANSWER.
 *
 * The row sits at the foot, under everything, so "Challenge with this" can only
 * be read as meaning the thing directly above it. Anything that reaches further
 * back is the app spending the reader's money on input they are not looking at.
 *
 * This replaced a two-step rule that skipped over a failed tail to the last turn
 * that succeeded, and the way that rule broke is the argument for this one. It
 * was written believing a failed turn shows nothing — but `ask` returns the text
 * that HAD arrived alongside its error, and the send path keeps both, so a
 * follow-up that streams half an answer and then loses the connection leaves
 * that half on screen with a warning under it. The row then sat beneath a
 * paragraph it was not about. That was the second finding of this shape after a
 * live tail was the first, which is the signal that the shape was wrong rather
 * than under-guarded: each fix was a state carved out of "reach further back",
 * and there was no reason to think the two of them were the last.
 *
 * The cost is that a failed follow-up now takes the row away until the reader
 * asks again, even when the failure produced no text and the answer above it is
 * perfectly good. That is the right way to be wrong here — a row that is absent
 * is a small annoyance a reader resolves by asking, and a row bound to the wrong
 * answer is a priced pass on the wrong input.
 *
 * Asking takes no lock, deliberately — that is the whole justification for the
 * channel — so nothing in the store is true during a chat stream and no refusal
 * that reads the store can catch any of this. It has to be decided here, off the
 * turns themselves.
 *
 * Structural in the turn, like `promotesFor` in the target: this module stays a
 * leaf and the engine's `ChatTurn` still goes straight in.
 */
export function promotableTurn<T extends { live?: boolean; error?: string; answer: string }>(
  turns: readonly T[],
): T | undefined {
  const last = turns.at(-1)
  if (!last || last.live || last.error || last.answer.trim() === '') return undefined
  return last
}
